<template>
  <v-container fluid class="flex-grow-1 overflow pt-1 pb-0 bodyTexts theme_surface_background">
    <v-row class="justify-content-center flex-grow-1">
      <v-col cols="12" class="pt-5 pt-0">
        <p class="pt-1">
          {{ $t('views.incidentForm.locationHelp') }}
        </p>
      </v-col>
      <v-col cols="12">
        <ZLocationInput
          v-model:location="search"
          v-model:longitude="incident.longitude"
          v-model:latitude="incident.latitude"
          geocoder-key="AIzaSyDtHsP85ZUuRHl2Zc0V7XzomSk1TCPKBl8"
          @geocoder-result="geocoderHandler">
          <v-row>
            <v-col cols="12">
              <ZLocationField
                :label="$t('common.actions.search')"
                :placeholder="$t('common.concepts.searchPlaceholder')"
                variant="outlined"
                clearable />
            </v-col>
            <v-col cols="6" class="bodyTexts">
              <ZLatitudeField
                variant="outlined"
                :label="$t('domain.fields.latitude')"
                density="compact"
                hide-details
                disabled
                color="rgb(var(--v-theme-lighten))"
                heigth="100px" />
            </v-col>
            <v-col cols="6" class="bodyTexts">
              <ZLongitudeField
                variant="outlined"
                :label="$t('domain.fields.longitude')"
                density="compact"
                hide-details
                disabled
                color="rgb(var(--v-theme-lighten))"
                heigth="100px" />
            </v-col>
          </v-row>
        </ZLocationInput>
      </v-col>

      <v-col cols="12">
        <v-text-field
          v-model="incident.address"
          variant="outlined"
          :label="$t('views.incidentForm.locationDescription')"
          density="compact"
          disabled
          hide-details
          color="rgb(var(--v-theme-lighten))"
          heigth="100px"></v-text-field>
      </v-col>
      <v-col cols="12">
        <v-text-field
          v-model="incident.country"
          variant="outlined"
          :label="$t('domain.fields.country')"
          density="compact"
          hide-details
          disabled
          color="rgb(var(--v-theme-lighten))"
          heigth="100px"></v-text-field>
      </v-col>
      <v-col cols="12">
        <v-text-field
          v-model="incident.state"
          variant="outlined"
          :label="$t('domain.fields.state')"
          density="compact"
          hide-details
          disabled
          color="rgb(var(--v-theme-lighten))"
          heigth="100px"></v-text-field>
      </v-col>
      <v-col cols="12">
        <v-text-field
          v-model="incident.county"
          variant="outlined"
          :label="$t('domain.fields.districtMunicipality')"
          density="compact"
          hide-details
          disabled
          color="rgb(var(--v-theme-lighten))"
          heigth="100px"></v-text-field>
      </v-col>
      <v-col cols="12" class="theme_background">
        <v-btn block color="rgb(var(--v-theme-primary))" variant="flat" @click="clearLocations">
          {{ $t('common.actions.clearLocation') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts" setup>
// Framework import
import type { PropType, Ref } from 'vue'
import { ref, watch } from 'vue'

import { storeToRefs } from 'pinia'

import { useVModel } from '@vueuse/core'
import { throttle } from 'lodash'

import { searchAddressBycoordinates } from '@tsyl/radon'
import type { RGeocoderResult } from '@tsyl/radon/lib/types/geocoder'

import MarkerStore from '@/stores/markerStore'

// Types import
import type { Incident } from '@/types/Incident'

const language = import.meta.env.VITE_DEFAULT_LANG as string
const glanguage = language.substring(0, 2)

const props = defineProps({
  modelValue: {
    type: Object as PropType<Partial<Incident>>,
    required: true
  },
  editting: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emits = defineEmits<{
  (event: 'closeIncidentLocationEvent'): void
  (event: 'openIncidentInfoEvent'): void
  (event: 'update:modelValue', v: Partial<Incident>): void
}>()

const incident = useVModel(props, 'modelValue', emits) as Ref<Partial<Incident>>

const markerStore = MarkerStore()
const { markerCoordinates } = storeToRefs(markerStore)

const search = ref<string | undefined>('')
const searchedAddress = ref([] as Array<string>)

const clearLocations = () => {
  incident.value.address = ''
  incident.value.country = ''
  incident.value.state = ''
  incident.value.county = ''
  incident.value.latitude = undefined
  incident.value.longitude = undefined

  search.value = ''

  searchedAddress.value = []
}

const searchLocationInfo = throttle(async function (lat: number, long: number) {
  const data = await searchAddressBycoordinates(import.meta.env.VITE_GOOGLE_API_KEY, lat, long, glanguage)
  incident.value.country = data.country
  incident.value.county = data.administrative_area_level_3
  incident.value.address = data.locality
  incident.value.state = data.administrative_area_level_1
}, 100)

watch([() => incident.value.longitude, () => incident.value.latitude], async ([long, lat]) => {
  if (long !== undefined && lat !== undefined) {
    if (validLatLon(lat, long)) {
      markerCoordinates.value = {
        latitude: lat,
        longitude: long
      }
      searchLocationInfo(lat, long)
    }
  } else {
    if (long === undefined && lat === undefined) clearLocations()
  }
})

const validLatLon = (lat: number, lon: number) => {
  return lat > -90 && lat < 90 && lon > -180 && lat < 180
}

function geocoderHandler(result: RGeocoderResult) {
  incident.value.country = result.country
  incident.value.county = result.administrative_area_level_3
  incident.value.address = result.locality
  incident.value.state = result.administrative_area_level_1
}
</script>

<style lang="scss"></style>

<style scoped lang="scss">
:deep(.v-field-label) {
  color: rgb(var(--v-theme-lighten));
}

:deep(.v-field__prepend-inner) {
  color: rgb(var(--v-theme-lighten));
}

.overflow {
  overflow-y: auto;
}

.betterMargins {
  margin-left: -16px;
  margin-right: -16px;
}
</style>
